import type { CaixaMovel, ConfigPlanner, ConfigPosicaoLivre } from '@miolos/core';

/**
 * As contas de mexer nas caixas posicionadas à mão.
 *
 * Ficam aqui, e não dentro de um componente, porque duas telas mexem no mesmo
 * mapa: as alças que se arrastam em cima da prévia e os campos de milímetros do
 * painel de ajuste. Assim as duas escrevem a posição do mesmo jeito.
 *
 * Nada aqui é obrigatório no miolo: sem nenhuma caixa o mapa não existe, e é
 * assim que o miolo antigo continua abrindo (campo opcional).
 */
export type MapaDePosicoes = NonNullable<ConfigPlanner['posicoesLivres']>;

/** O mapa como ele está, sem inventar entradas. */
export function posicoesDo(config: ConfigPlanner): MapaDePosicoes {
  return config.posicoesLivres ?? {};
}

/** Quantas caixas já têm posição — é o que desloca a próxima que nasce. */
export function quantasPosicionadas(config: ConfigPlanner): number {
  return Object.values(posicoesDo(config)).filter(Boolean).length;
}

/** Guarda a posição de uma caixa. */
export function comPosicao(
  config: ConfigPlanner,
  chave: CaixaMovel,
  posicao: ConfigPosicaoLivre,
): MapaDePosicoes {
  return { ...posicoesDo(config), [chave]: posicao };
}

/** Devolve a caixa para o lugar que o motor calcula (sai do mapa). */
export function semPosicao(config: ConfigPlanner, chave: CaixaMovel): MapaDePosicoes {
  const mapa = { ...posicoesDo(config) };
  delete mapa[chave];
  return mapa;
}

/**
 * Põe a caixa na frente das outras.
 *
 * A camada é um número na própria posição, e não a ordem das chaves do mapa: o
 * banco guarda a configuração em `jsonb`, que reordena as chaves do objeto — a
 * caixa voltaria para trás ao reabrir o miolo.
 */
export function trazidaParaAFrente(config: ConfigPlanner, chave: CaixaMovel): MapaDePosicoes {
  const mapa = posicoesDo(config);
  const camadas = Object.values(mapa)
    .filter((posicao): posicao is ConfigPosicaoLivre => Boolean(posicao))
    .map((posicao) => posicao.z ?? 0);
  const maior = camadas.length > 0 ? Math.max(...camadas) : 0;

  const atual = mapa[chave] ?? null;
  if (!atual) return mapa;

  return { ...mapa, [chave]: { ...atual, z: maior + 1 } };
}

/** Qual caixa está mais à frente (para o botão dizer o que faz). */
export function camadaDa(config: ConfigPlanner, chave: CaixaMovel): number {
  return posicoesDo(config)[chave]?.z ?? 0;
}
